/**
 * Test cases — by requirement, in plain words. The left list is the
 * requirement specification (sections, from `/coverage`) plus the screens
 * whose cases cite no requirement; the right side shows one requirement: its
 * business rules, the rules no case proves yet, and each case as the steps a
 * person would follow. The case file itself (JSON) is for specialists.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { CheckCircle2, Play, Users } from 'lucide-react'
import Button from '@/components/common/Button'
import {
  apiError, getCase, listCases, validateCases,
  type CaseRow, type Coverage, type CoverageRule, type Issue, type TestCase, type TestPlan, type ValidateResult,
} from '@/api/uiTesting'
import type { Focus, LastResult, TestingNav } from './TestingPage'
import CaseEditor, { newCaseTemplate } from './CaseEditor'
import PersonasModal from './PersonasModal'
import { Badge, Dot, IssuesTable, TONE, ago, caseTone, inputStyle, labelCaps, mutedText, panel, type Tone } from './shared'
import { caseTypeText, isCheck, nice, originText, personaText, stepText } from './plainSteps'

interface Props {
  project: string
  canTest: boolean
  plan: TestPlan | null
  coverage: Coverage | null
  lastResults: Record<string, LastResult>
  focus: Focus
  nav: TestingNav
  onReload: () => Promise<void> | void
}

/** One entry of the left list: a requirement, or a screen's uncited cases. */
interface Group {
  key: string
  id: string
  title: string
  module: string
  object: string
  description?: string
  caseIds: string[]
  rules: CoverageRule[]
  kind: 'requirement' | 'screen'
}

interface GroupState { tone: Tone; text: string }

export default function CasesTab({ project, canTest, plan, coverage, lastResults, focus, nav, onReload }: Props) {
  const [rows, setRows] = useState<CaseRow[]>([])
  const [unreadable, setUnreadable] = useState<Issue[]>([])
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [picked, setPicked] = useState<string | null>(focus.requirement ?? null)
  const [full, setFull] = useState<Record<string, TestCase>>({})
  const [editing, setEditing] = useState<{ id: string; initial?: TestCase } | null>(null)
  const [personasOpen, setPersonasOpen] = useState(false)
  const [validation, setValidation] = useState<ValidateResult | null>(null)
  const [validating, setValidating] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await listCases(project)
      setRows(r.cases)
      setUnreadable(r.unreadable)
      setFull({})
    } catch (err) {
      toast.error(apiError(err, 'Could not load the test cases'))
    } finally {
      setLoading(false)
    }
  }, [project])

  useEffect(() => { void load() }, [load])
  useEffect(() => { if (focus.requirement) setPicked(focus.requirement) }, [focus.requirement])

  const byId = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows])

  const groups = useMemo<Group[]>(() => {
    const out: Group[] = []
    const cited = new Set<string>()
    if (coverage?.frs_found) {
      for (const s of coverage.matrix) {
        const ids = s.cases.filter((id) => byId.has(id))
        ids.forEach((id) => cited.add(id))
        out.push({
          key: s.key, id: s.id, title: s.title, module: s.module || 'Other', object: s.object,
          description: s.description, caseIds: ids, kind: 'requirement',
          rules: (coverage.rule_matrix || []).filter((r) => (r.section_key ?? '') === s.key
            || (!r.section_key && r.section === s.title)),
        })
      }
    }
    const byObject = new Map<string, string[]>()
    for (const r of rows) {
      if (cited.has(r.id)) continue
      const k = r.object || (r.surface === 'site' ? 'Website' : 'Other')
      byObject.set(k, [...(byObject.get(k) || []), r.id])
    }
    for (const [obj, ids] of Array.from(byObject.entries()).sort((a, b) => a[0].localeCompare(b[0]))) {
      out.push({
        key: `screen:${obj}`, id: 'Screen', title: nice(obj, true), object: obj, caseIds: ids, rules: [],
        module: coverage?.frs_found ? 'Not linked to a requirement' : 'Screens', kind: 'screen',
      })
    }
    return out
  }, [coverage, rows, byId])

  const stateOf = useCallback((g: Group): GroupState => {
    const n = g.caseIds.length
    if (!n) return { tone: 'warn', text: 'no test yet' }
    const res = g.caseIds.map((id) => lastResults[id]?.status)
    const failing = res.filter((s) => s === 'failed').length
    if (failing) return { tone: 'err', text: `${n} case${n === 1 ? '' : 's'} · ${failing} failing` }
    if (res.every((s) => s === 'passed')) return { tone: 'ok', text: `${n} case${n === 1 ? '' : 's'} · all pass` }
    if (res.every((s) => !s)) return { tone: 'neutral', text: `${n} case${n === 1 ? '' : 's'} · not run yet` }
    return { tone: 'warn', text: `${n} case${n === 1 ? '' : 's'} · some not run` }
  }, [lastResults])

  const shownGroups = groups.filter((g) => {
    if (!q) return true
    const hay = `${g.id} ${g.title} ${g.object} ${g.module} ${g.caseIds.join(' ')}`.toLowerCase()
    return hay.includes(q.toLowerCase())
  })

  const current = groups.find((g) => g.key === picked) ?? shownGroups[0] ?? null

  // The case files of the group on screen — loaded once each.
  useEffect(() => {
    if (!current) return
    const missing = current.caseIds.filter((id) => !full[id])
    if (!missing.length) return
    let alive = true
    Promise.all(missing.map((id) => getCase(project, id).then((c) => [id, c] as const).catch(() => null)))
      .then((got) => {
        if (!alive) return
        setFull((f) => {
          const next = { ...f }
          for (const g of got) if (g) next[g[0]] = g[1]
          return next
        })
      })
    return () => { alive = false }
  }, [project, current, full])

  const changed = async () => { await load(); await onReload() }

  const onValidate = async () => {
    setValidating(true)
    try {
      const r = await validateCases(project)
      setValidation(r)
      if (r.ok) toast.success(`All ${r.cases} cases are valid`)
      else toast.error(`${r.errors} problem(s) in the cases — see below`)
    } catch (err) {
      toast.error(apiError(err, 'Could not check the cases'))
    } finally {
      setValidating(false)
    }
  }

  const writeMyself = (g: Group, rule?: CoverageRule) => setEditing({
    id: '__new__',
    initial: newCaseTemplate({ object: g.object, ruleId: rule?.id ?? (g.kind === 'requirement' ? g.id : undefined), ruleText: rule?.rule }),
  })

  let lastModule = ''

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' }}>
      <aside aria-label="Requirements" style={{
        ...panel, flex: '1 1 300px', minWidth: 0, padding: 12, display: 'flex', flexDirection: 'column', gap: 2,
        maxHeight: 'calc(100vh - 220px)', overflowY: 'auto', position: 'sticky', top: 12,
      }}>
        <label htmlFor="tw-find" style={{ ...labelCaps, padding: '6px 6px 0' }}>Find a requirement or screen</label>
        <input id="tw-find" type="search" value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="e.g. hearing, court forum"
               style={{ ...inputStyle, margin: '6px 6px 10px', minHeight: 40, padding: '0 12px', fontSize: 15 }} />
        {loading && <div style={{ ...mutedText, padding: 8 }}>Loading…</div>}
        {!loading && shownGroups.length === 0 && (
          <div style={{ ...mutedText, padding: 8 }}>{rows.length ? 'Nothing matches.' : 'No test cases yet.'}</div>
        )}
        {shownGroups.map((g) => {
          const st = stateOf(g)
          const on = current?.key === g.key
          const heading = g.module !== lastModule ? g.module : null
          lastModule = g.module
          return (
            <div key={g.key}>
              {heading && (
                <div style={{ ...labelCaps, fontSize: 13, padding: '12px 14px 4px' }}>{heading}</div>
              )}
              <button type="button" onClick={() => setPicked(g.key)} aria-current={on} style={{
                width: '100%', textAlign: 'left', padding: '10px 14px', borderRadius: 8, minHeight: 44,
                display: 'flex', gap: 10, alignItems: 'center', color: 'var(--color-text)',
                background: on ? 'var(--color-accent-weak)' : 'transparent',
              }}>
                <Dot tone={st.tone} />
                <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontWeight: 600, fontSize: 15 }}>{g.title}</span>
                  <span style={{ ...mutedText }}>{g.kind === 'requirement' ? `${g.id} · ` : ''}{st.text}</span>
                </span>
              </button>
            </div>
          )
        })}
      </aside>

      <section aria-label="Requirement" style={{ flex: '999 1 620px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {!current ? (
          <div style={{ ...panel, padding: 24, display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
            <h2 style={{ margin: 0, fontSize: 21, fontWeight: 600 }}>No test cases yet</h2>
            <p style={{ margin: 0, color: 'var(--color-text-secondary)', lineHeight: 1.55 }}>
              Refresh the generated cases to make the baseline from the screens, or ask AI to write cases from the
              requirement specification.
            </p>
          </div>
        ) : (
          <GroupView project={project} g={current} byId={byId} full={full} lastResults={lastResults} canTest={canTest}
                     nav={nav} onEdit={(id) => setEditing({ id })} onWriteMyself={(r) => writeMyself(current, r)} />
        )}

        {(unreadable.length > 0 || validation) && (
          <section style={{ ...panel, padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {unreadable.length > 0 && (
              <>
                <strong>{unreadable.length} case file(s) can’t be read</strong>
                <IssuesTable issues={unreadable} />
              </>
            )}
            {validation && (
              <>
                <strong>
                  {validation.ok ? `All ${validation.cases} cases are valid` : `${validation.errors} problem(s) in the cases`}
                  {validation.warnings ? ` · ${validation.warnings} warning(s)` : ''}
                </strong>
                <IssuesTable issues={validation.issues} />
              </>
            )}
          </section>
        )}

        <details style={{ ...panel, padding: '14px 20px' }}>
          <summary style={{ cursor: 'pointer', color: 'var(--color-text-secondary)', fontSize: 15 }}>
            For specialists: check the case files, personas, and the JSON behind each case
          </summary>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
            <p style={{ margin: 0, ...mutedText, fontSize: 14, lineHeight: 1.5 }}>
              Every case is a file under <code>Test_Design/acceptance/cases/</code>. <b>Edit</b> on a case opens it;
              it is checked against the application’s screens when you save. A case runs as one persona —
              the owner, or a temporary user holding the persona’s roles.
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Button variant="secondary" icon={<CheckCircle2 size={15} />} loading={validating} onClick={() => void onValidate()}>
                Check all cases
              </Button>
              <Button variant="secondary" icon={<Users size={15} />} onClick={() => setPersonasOpen(true)}>Personas</Button>
            </div>
          </div>
        </details>
      </section>

      {editing && (
        <CaseEditor project={project} caseId={editing.id} initial={editing.initial} canTest={canTest}
                    onClose={() => setEditing(null)} onChanged={changed} />
      )}
      {personasOpen && (
        <PersonasModal project={project} plan={plan} canTest={canTest}
                       onClose={() => setPersonasOpen(false)} onSaved={onReload} />
      )}
    </div>
  )
}

function GroupView({ project, g, byId, full, lastResults, canTest, nav, onEdit, onWriteMyself }: {
  project: string; g: Group; byId: Map<string, CaseRow>; full: Record<string, TestCase>
  lastResults: Record<string, LastResult>; canTest: boolean; nav: TestingNav
  onEdit: (id: string) => void; onWriteMyself: (rule?: CoverageRule) => void
}) {
  const untested = g.rules.filter((r) => !r.cases.length)
  const tested = g.rules.length - untested.length
  return (
    <>
      <div style={{ ...panel, padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <span style={{ color: 'var(--color-text-secondary)', fontSize: 14 }}>
          {g.kind === 'requirement' ? `${g.id} · ${g.object ? nice(g.object, true) : g.module}` : 'Cases for this screen that cite no requirement'}
        </span>
        <h2 style={{ margin: 0, fontSize: 23, fontWeight: 600 }}>{g.title}</h2>
        {g.description && (
          <p style={{ margin: 0, color: 'var(--color-text-secondary)', lineHeight: 1.55, fontSize: 15 }}>{g.description}</p>
        )}
        {g.rules.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Badge tone={untested.length ? 'neutral' : 'ok'} style={{ borderRadius: 6 }}>
              {g.rules.length} rule{g.rules.length === 1 ? '' : 's'} · {tested} tested
            </Badge>
            {untested.length > 0 && (
              <Badge tone="warn" style={{ borderRadius: 6 }}>
                {untested.length} rule{untested.length === 1 ? ' has' : 's have'} no test
              </Badge>
            )}
          </div>
        )}
      </div>

      {g.caseIds.length === 0 && untested.length === 0 && (
        <Gap title="No test yet for this requirement" sub="Ask AI can write cases for it from the specification."
             canTest={canTest} onAi={() => nav.askAi(`for the requirement ${g.id} “${g.title}”`)}
             onMyself={() => onWriteMyself()} />
      )}
      {untested.map((r) => (
        <Gap key={r.key} title={`No test yet: “${r.rule}”`} sub={`Business rule ${r.id} of this requirement.`}
             canTest={canTest} onAi={() => nav.askAi(`for business rule ${r.id} of “${g.title}”: ${r.rule}`)}
             onMyself={() => onWriteMyself(r)} />
      ))}

      {g.caseIds.map((id) => (
        <CaseCard key={id} id={id} row={byId.get(id)} c={full[id]} last={lastResults[id]} canTest={canTest}
                  onRun={() => void nav.runCases([id])} onEdit={() => onEdit(id)}
                  onOpenRun={(runId) => nav.go('runs', { run: runId, caseId: id })} project={project} />
      ))}
    </>
  )
}

function Gap({ title, sub, canTest, onAi, onMyself }: {
  title: string; sub: string; canTest: boolean; onAi: () => void; onMyself: () => void
}) {
  return (
    <div style={{
      border: '1px dashed var(--color-warning)', background: 'var(--color-warning-weak)', borderRadius: 12,
      padding: '16px 20px', display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap',
    }}>
      <div style={{ flex: '1 1 340px', display: 'flex', flexDirection: 'column', gap: 4 }}>
        <strong style={{ fontSize: 15 }}>{title}</strong>
        <span style={{ color: 'var(--color-text-secondary)', fontSize: 14 }}>{sub}</span>
      </div>
      {canTest && (
        <>
          <Button variant="secondary" onClick={onAi}>Write it with AI</Button>
          <Button variant="ghost" onClick={onMyself}>Write it myself</Button>
        </>
      )}
    </div>
  )
}

const RESULT_WORD: Record<string, string> = { passed: 'Passed', failed: 'Failed', blocked: 'Couldn’t run' }

function CaseCard({ id, row, c, last, canTest, onRun, onEdit, onOpenRun }: {
  project: string; id: string; row?: CaseRow; c?: TestCase; last?: LastResult; canTest: boolean
  onRun: () => void; onEdit: () => void; onOpenRun: (runId: string) => void
}) {
  const title = String(c?.title ?? row?.title ?? id)
  const pre = Array.isArray(c?.preconditions) ? (c!.preconditions as unknown[]) : []
  const steps = Array.isArray(c?.steps) ? (c!.steps as unknown[]) : []
  const tone = caseTone(last?.status)
  return (
    <article style={{ ...panel, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>{title}</h3>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <Badge style={{ borderRadius: 6, fontSize: 13 }}>{caseTypeText(row?.type)}</Badge>
            <Badge style={{ borderRadius: 6, fontSize: 13 }}>{personaText(row?.persona)}</Badge>
            <Badge tone={row?.origin === 'agent' ? 'info' : 'neutral'} style={{ borderRadius: 6, fontSize: 13 }}>
              {originText(row?.origin)}
            </Badge>
            {row && row.errors > 0 && (
              <Badge tone="err" style={{ borderRadius: 6, fontSize: 13 }}>{row.errors} problem{row.errors === 1 ? '' : 's'} — won’t run</Badge>
            )}
            {last ? (
              <button type="button" onClick={() => onOpenRun(last.runId)} style={{
                color: TONE[tone].fg, fontSize: 13, fontWeight: 600, padding: '0 4px',
              }}>● {RESULT_WORD[last.status] ?? last.status} {ago(last.at)}</button>
            ) : <span style={{ ...mutedText, fontWeight: 600 }}>● Not run yet</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {canTest && <Button variant="secondary" icon={<Play size={14} />} onClick={onRun}>Run</Button>}
          <Button variant="ghost" onClick={onEdit}>{canTest ? 'Edit' : 'View'}</Button>
        </div>
      </div>
      {!c ? <div style={mutedText}>Loading the steps…</div> : (
        <ol style={{ margin: 0, paddingLeft: 22, display: 'flex', flexDirection: 'column', gap: 6, lineHeight: 1.45, fontSize: 15 }}>
          {pre.map((s, i) => (
            <li key={`p${i}`} style={{ color: 'var(--color-text-secondary)' }}>Before the test: {stepText(s)}</li>
          ))}
          {steps.map((s, i) => (
            <li key={`s${i}`} style={{ fontWeight: isCheck(s) ? 600 : 400 }}>
              {stepText(s)}
              {s && typeof s === 'object' && (s as Record<string, unknown>).note ? (
                <span style={{ ...mutedText, fontWeight: 400 }}> — {String((s as Record<string, unknown>).note)}</span>
              ) : null}
            </li>
          ))}
        </ol>
      )}
      <span style={{ ...mutedText, fontFamily: 'var(--font-mono)', fontSize: 13 }}>{id}</span>
    </article>
  )
}
